import React, { useCallback, useMemo, useState } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { DeliveryStatus, RbiOrderStatus, useGetUserOrdersQuery } from 'generated/rbi-graphql';
import { useNavigation } from 'hooks/navigation/use-navigation';
import { appendObjectToQueryString } from 'hooks/navigation/utils';
import { serviceModeToIntlId } from 'pages/store-locator/new-ui/store-card/hooks/use-service-mode-text';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { ServiceMode, useOrderContext } from 'state/order';
import { useReversedUIContext } from 'state/reversed-ui';
import { useStoreContext } from 'state/store';
import { theme } from 'styles/configure-theme';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { routes } from 'utils/routing';

import CtaButton from '../actions/cta-button';
import { CurbsideConfirmationModal } from '../curbside-confirmation-modal';
import { ITopServiceModeDetail, TopServiceVariants } from '../types';

export const StyledIcon = Icon.withConfig({
  size: '5',
  ...hiddenAccessibilityPlatformProps,
});

export const useTopServiceModeDetails = (): ITopServiceModeDetail => {
  const {
    serviceMode,
    deliveryAddress,
    curbsidePickupOrderId,
    isPreConfirmCurbside,
  } = useOrderContext();
  const { store } = useStoreContext();
  const enableOrdering = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);
  const [curbsideConfirmationModalOpen, setCurbsideConfirmationModalOpen] = useState<boolean>(
    false
  );
  const { formatMessage } = useIntl();
  const { logEvent } = useMParticleContext();
  const { reversedUI } = useReversedUIContext();
  const { isAuthenticated } = useAuthContext();

  const iconColor = reversedUI ? theme.token('icon-reversed') : theme.token('icon-default');
  const { linkTo, navigate } = useNavigation();

  const { data, refetch } = useGetUserOrdersQuery({
    variables: {
      limit: 1,
      orderStatuses: [
        RbiOrderStatus.INSERT_SUCCESSFUL,
        RbiOrderStatus.UPDATE_SUCCESSFUL,
        RbiOrderStatus.REFUND_SUCCESSFUL,
      ],
    },
    skip: !isAuthenticated,
  });

  const isCurrentDeliveryOrder = useMemo(() => {
    const deliveryOrder = data?.userOrders?.orders?.[0];
    const orderStatus = deliveryOrder?.delivery?.status;
    const failedOrderStatus = [
      DeliveryStatus.ORDER_CANCELLED,
      DeliveryStatus.ORDER_ABANDONED,
      DeliveryStatus.ORDER_DROPPED_OFF,
    ];

    if (!deliveryOrder || !orderStatus || failedOrderStatus.includes(orderStatus)) {
      return null;
    }
    return deliveryOrder;
  }, [data]);

  const handleServiceModeChange = useCallback(() => {
    logEvent(CustomEventNames.TOP_SERVICE_MODE, EventTypes.Other, {
      'Service Mode': serviceMode,
    });

    if (isPreConfirmCurbside() && isAuthenticated) {
      return setCurbsideConfirmationModalOpen(true);
    }

    navigate(routes.serviceMode);
  }, [isAuthenticated, isPreConfirmCurbside, logEvent, navigate, serviceMode]);

  const handleNavigateToOrderConfirmation = (rbiOrderId: string, showToast?: boolean) => {
    logEvent(CustomEventNames.BOTTOM_SERVICE_MODE, EventTypes.Other, {
      'Service Mode': serviceMode,
      'Order Id': rbiOrderId,
    });
    linkTo(appendObjectToQueryString(`${routes.orderConfirmation}/${rbiOrderId}`, { showToast }));
  };

  const getCtaButton = () => {
    if (isPreConfirmCurbside()) {
      return (
        <CtaButton
          text="confirmArrival"
          cta={() => handleNavigateToOrderConfirmation(curbsidePickupOrderId, true)}
        />
      );
    } else if (isCurrentDeliveryOrder) {
      return (
        <CtaButton
          text="trackOrder"
          cta={() => handleNavigateToOrderConfirmation(isCurrentDeliveryOrder.rbiOrderId)}
        />
      );
    }
    return <CtaButton text="change" cta={handleServiceModeChange} />;
  };

  const resultBase: ITopServiceModeDetail = {
    heading: formatMessage({ id: 'forItemAvailability' }),
    details: null,
    icon: <StyledIcon variant="location" color={iconColor} />,
    button: null,
    onTopServiceClick: handleServiceModeChange,
    variant: TopServiceVariants.DEFAULT,
    CurbsideConfirmationModal,
    curbsideConfirmationModalOpen,
    setCurbsideConfirmationModalOpen,
    refetchUserOrder: refetch,
  };

  const CtaBtn = getCtaButton();

  if (store._id) {
    if ((!store.hasMobileOrdering || !enableOrdering) && store.physicalAddress?.address1) {
      return {
        ...resultBase,
        button: CtaBtn,
        heading: formatMessage({ id: 'yourSelectedStore' }),
        details: store.physicalAddress.address1,
        icon: <StyledIcon variant="restaurant" color={iconColor} />,
      };
    }
    switch (serviceMode) {
      case ServiceMode.DELIVERY:
        if (deliveryAddress) {
          return {
            ...resultBase,
            button: CtaBtn,
            heading: formatMessage({ id: 'delivery' }),
            details: deliveryAddress.addressLine1,
            icon: <StyledIcon variant="delivery" color={iconColor} />,
          };
        }
        break;
      case ServiceMode.CURBSIDE:
        if (store.physicalAddress?.address1) {
          return {
            ...resultBase,
            button: CtaBtn,
            heading: formatMessage({ id: 'curbside' }),
            details: store.physicalAddress.address1,
            icon: <StyledIcon variant="restaurant" color={iconColor} />,
          };
        }
        break;
      case ServiceMode.TAKEOUT:
      case ServiceMode.DRIVE_THRU:
      case ServiceMode.EAT_IN:
        if (store.physicalAddress?.address1) {
          return {
            ...resultBase,
            button: CtaBtn,
            heading: formatMessage({ id: serviceModeToIntlId[serviceMode] }),
            details: store.physicalAddress.address1,
            icon: <StyledIcon variant="restaurant" color={iconColor} />,
          };
        }
        break;
      default:
    }
  }
  return {
    ...resultBase,
    variant: TopServiceVariants.NO_STORE,
  };
};

export const TSM_GET_USER_ORDER_STATUSES = [
  RbiOrderStatus.INSERT_SUCCESSFUL,
  RbiOrderStatus.UPDATE_SUCCESSFUL,
  RbiOrderStatus.REFUND_SUCCESSFUL,
];
